// @flow

import { injectIntl } from "react-intl"
import styled from "styled-components"
import { DefaultButton } from "@cabonline/shared/components"
import { Button, Col, Modal, Row, Spin } from "antd"
import React, { useEffect, useState } from "react"
import { Link, withRouter } from "react-router-dom"
import { observer } from "mobx-react-lite"
import OrderSearchStore from "shared/stores/OrderSearchStore"
import WideSpinner from "shared/components/WideSpinner"
import WideCard from "@cabonline/shared/components/WideCard/WideCard"
import { get, isEmpty, isUndefined, join } from "lodash"
import type { Address, Booker, Product } from "shared/types/OrdersType"
import { TRIP_STATUS } from "shared/types/OrdersType"
import type { Driver } from "shared/types/DriverType"
import type { Vehicle } from "shared/types/VehicleType"
import EventPanel from "apps/orders/EventPanel"
import OrderHandlePanel from "apps/orders/OrderHandlePanel"
import TrackedMap from "shared/components/maps/TrackedMap/TrackedMap"
import type { Coords } from "shared/types/Coords"
import { messageError } from "shared/utils/errors"
import moment from "moment"
import { toTitleCase } from "shared/utils/stringUtils"
import PaymentPanel from "apps/orders/PaymentPanel"
import OrderSearchUtils from "apps/orders/OrderSearchUtils"
import DotsDropdown from "@cabonline/shared/components/DotsDropdown/DotsDropdown"
import RatingPanel from "apps/orders/RatingPanel"
import ConfirmCancelOrderModal from "apps/orders/ConfirmCancelOrderModal"
import UserStore from "shared/stores/UserStore"

const Container = styled.div`
  display: flex;
  flex-flow: column;
  padding: 24px;
`

const Header = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
`

const ContentRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
`

const ContentColLeft = styled(Col)`
  display: flex;
  flex-flow: column wrap;
  justify-content: space-between;
  width: 74%;
`

const ContentColRight = styled(Col)`
  display: flex;
  flex-flow: column wrap;
  width: 24%;
`

const OrderInfoDiv = styled.div`
  display: flex;
  align-self: flex-start;
  flex-flow: column wrap;
  width: 74%;
`

const ButtonDiv = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-end;
  width: 24%;
`

const PublicIdRow = styled.div`
  display: flex;
  flex-flow: row wrap;
`

const PublicIdDiv = styled.div`
  align-self: flex-start;

  height: 27px;
  font-size: 20px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
`

const FailedText = styled.span`
  color: #bf0000;
`

const FrogneIdRow = styled(Row)`
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;

  height: 16px;
  font-size: 12px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #979797;
  width: 100%;
`

const FrogneId = styled.div`
  align-self: flex-start;

  margin-right: 15px;
  font-size: 13px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #989898;
`

const InfoContainer = styled.div`
  border-radius: 6px;
  box-shadow: 0 2px 16px 0 #f3f3f3;
  background-color: #ffffff;
  padding-top: 32px;
  padding-bottom: 32px;
  padding-left: 24px;
  padding-right: 24px;
  margin-top: 12px;
  margin-bottom: 12px;
`

const FormContainer = styled.div`
  width: 100%;
  margin: 10px;
`

const FormRow = styled(Row)`
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
`

const FormLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #989898;
`
const FormValue = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #000000;
`

const FormValueBlue = styled.div`
  font-size: 16px;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  font-weight: 600;
`

const Btn = styled.div`
  position: absolute;
  width: 40%;
  transform: translate(90%, -110%);
  -ms-transform: translate(80%, -90%);
`

const MapContainer = styled.div`
  height: 480px;
  width: 100%;
  padding-top: 20px;
`

const OrderDetailView = props => {
  const {
    intl: { formatMessage },
    orderId
  } = props

  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [isConfirmCancelModalOpen, setConfirmCancelModalOpen] = useState(false)
  const [ajaxPending, setAjaxPending] = useState(false)
  const { detailedOrder } = OrderSearchStore

  useEffect(() => {
    setLoading(true)
    OrderSearchStore.clearDetailedOrder()
    OrderSearchStore.findDetailedOrderById(orderId)
      .then(() => setLoading(false))
      .catch(e => {
        setLoading(false)
        messageError("order-search", e)
      })
  }, [])

  const getCoordinates = (address: Address): ?Coords => {
    const latitude = get(address, "latitude")
    const longitude = get(address, "longitude")

    if (!latitude || !longitude) return

    return {
      lat: latitude,
      lng: longitude
    }
  }

  const driverObjToName = (driver: Driver) => {
    return `${get(driver, "firstName", "-")} ${get(driver, "lastName", "")}`
  }

  const bookerObjToName = (booker: Booker) => {
    return `${get(booker, "firstName", "-")} ${get(booker, "lastName", "")}`
  }

  const vehicleObjToText = (vehicle: Vehicle) => {
    return get(vehicle, "manufacturer", "-") + get(vehicle, "model", "")
  }

  const renderOrderSummary = (
    from: Address,
    via: Array<Address>,
    to: Address,
    pickUpTime: string,
    travelNow: boolean,
    product: Product,
    messageToDriver: string,
    price: string,
    currency: string,
    fleetName: string
  ) => {
    const carOptions = get(product, "optionIds", []).map(o =>
      formatMessage({ id: "orderAttributes." + o })
    )
    const viaAddressList = via.map(add =>
      OrderSearchUtils.addressObjToText(add, true)
    )

    const fromCoordinates = getCoordinates(from)
    const toCoordinates = getCoordinates(to)
    const viaCoordinates = via.map(obj => getCoordinates(obj)).filter(Boolean)
    return (
      <FormContainer>
        <FormRow>
          <Col span={12}>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.from" })}
            </FormLabel>
          </Col>
          <Col>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.productnoptions" })}
            </FormLabel>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormValue>
              {OrderSearchUtils.addressObjToText(from, true)}
            </FormValue>
          </Col>
          <Col span={12}>
            <FormValue>
              {!isUndefined(get(product, "subProductId"))
                ? formatMessage({
                    id: "orderAttributes." + get(product, "subProductId")
                  })
                : "-"}
              {join(carOptions, ", ")}
            </FormValue>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.via" })}
            </FormLabel>
          </Col>
          <Col span={12}>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.messageToDriver" })}
            </FormLabel>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            {viaAddressList.length > 0 &&
              viaAddressList.map((address, index) => (
                <FormValue key={index}>{address}</FormValue>
              ))}
            {viaAddressList.length === 0 && <FormValue>-</FormValue>}
          </Col>
          <Col span={12}>
            <FormValue>{messageToDriver}</FormValue>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.to" })}
            </FormLabel>
          </Col>
          <Col span={12}>
            <FormLabel>{formatMessage({ id: "common.fleet" })}</FormLabel>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormValue>{OrderSearchUtils.addressObjToText(to, true)}</FormValue>
          </Col>
          <Col span={12}>
            <FormValue>{fleetName}</FormValue>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormLabel>
              {formatMessage({
                id: "apps.order-search.search-result.pickup-time"
              })}
            </FormLabel>
          </Col>
          <Col span={12} />
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormValue>
              {travelNow
                ? moment(pickUpTime).format("YYYY-MM-DD HH:mm")
                : `${moment(pickUpTime).format(
                    "YYYY-MM-DD HH:mm"
                  )} (${formatMessage({
                    id: "apps.order-search.label.prebooked"
                  })})`}
            </FormValue>
          </Col>
        </FormRow>
        <FormRow>
          <div style={{ height: 373, width: "100%" }}>
            <TrackedMap
              from={fromCoordinates}
              to={toCoordinates}
              via={viaCoordinates}
              departureTime={moment(pickUpTime).toISOString(true)}
              locked={true}
              style
              isAnimatedPath={false}
            />
            <Btn>
              <DefaultButton onClick={() => setModalOpen(true)}>
                {formatMessage({
                  id: "apps.order-search.button.interactive.map"
                })}
              </DefaultButton>
            </Btn>
          </div>
          <Modal
            visible={modalOpen}
            title={null}
            footer={null}
            onCancel={() => setModalOpen(false)}
            maskClosable={true}
            width={807}
          >
            <MapContainer data-cy="cy_interactiveMap">
              <TrackedMap
                from={fromCoordinates}
                to={toCoordinates}
                departureTime={moment(pickUpTime).toISOString(true)}
                locked={false}
                via={viaCoordinates}
                isAnimatedPath={false}
              />
            </MapContainer>
          </Modal>
        </FormRow>
      </FormContainer>
    )
  }

  const renderTravelerSummary = (
    traveler: string,
    travelerPhone: string,
    travelerId: number,
    driver: string,
    driverId: string,
    driverPhone: string,
    tripBookedBy: string,
    tripBookedById: string,
    tripBookedByPhone: string,
    registrationNumber: string,
    taxiNumber: string,
    carMake: string
  ) => {
    return (
      <FormContainer>
        <FormRow>
          <Col span={12}>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.traveler" })}
            </FormLabel>
          </Col>
          <Col>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.driver" })}
            </FormLabel>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormValueBlue>
              {UserStore.hasPermissions(["user:traveler-view"]) &&
              travelerId ? (
                <Link to={`/dashboard/users/travelers/view/${travelerId}`}>
                  {toTitleCase(traveler)}
                </Link>
              ) : (
                toTitleCase(traveler)
              )}
            </FormValueBlue>
          </Col>
          <Col>
            <FormValueBlue>
              {UserStore.hasPermissions(["driver-registry:view"]) &&
              driverId ? (
                <Link to={`/dashboard/driver/view/${driverId}`}>
                  {toTitleCase(driver)}
                </Link>
              ) : (
                toTitleCase(driver)
              )}
            </FormValueBlue>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormValue>{travelerPhone}</FormValue>
          </Col>
          <Col>
            <FormValue>{driverPhone}</FormValue>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.bookedBy" })}
            </FormLabel>
          </Col>
          <Col>
            <FormLabel>
              {formatMessage({ id: "apps.order-search.label.car" })}
            </FormLabel>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormValueBlue>
              {UserStore.hasPermissions(["user:traveler-view"]) &&
              tripBookedById ? (
                <Link to={`/dashboard/users/travelers/view/${tripBookedById}`}>
                  {toTitleCase(tripBookedBy)}
                </Link>
              ) : (
                toTitleCase(tripBookedBy)
              )}
            </FormValueBlue>
          </Col>
          <Col>
            <FormValueBlue>{taxiNumber}</FormValueBlue>
          </Col>
        </FormRow>
        <FormRow>
          <Col span={12}>
            <FormValue>{tripBookedByPhone}</FormValue>
          </Col>
          <Col>
            <FormValue>
              {registrationNumber} - {carMake}
            </FormValue>
          </Col>
        </FormRow>
      </FormContainer>
    )
  }

  const updateSelectedOrderToCancelled = () => {
    const updatedOrder = {
      ...OrderSearchStore.selectedOrder,
      state: TRIP_STATUS.CANCELLED,
      status: "HANDLED",
      cancellable: false,
      modifiable: false
    }
    OrderSearchStore.setSelectedOrder(updatedOrder)
  }

  const updateOrderInListToCancelled = () => {
    const updatedOrder = {
      ...OrderSearchStore.selectedOrder,
      state: TRIP_STATUS.CANCELLED,
      status: "HANDLED",
      cancellable: false,
      modifiable: false
    }
    const orders = OrderSearchStore.orders
    OrderSearchStore.setOrders(
      orders.map(o =>
        o.orderId === OrderSearchStore.selectedOrder.orderId
          ? Object.assign({}, o, updatedOrder)
          : o
      )
    )
  }

  const onCancelOrderConfirmed = () => {
    setConfirmCancelModalOpen(false)
    setAjaxPending(true)
    OrderSearchStore.cancelOrder(get(detailedOrder, "id"))
      .then(() => {
        updateSelectedOrderToCancelled()
        updateOrderInListToCancelled()
        setAjaxPending(false)
      })
      .catch(e => {
        messageError("Failed to cancel order", e)
        setAjaxPending(false)
      })
  }

  const onCancelOrderRejected = () => {
    setConfirmCancelModalOpen(false)
  }

  const onCancelOrderClicked = () => {
    setConfirmCancelModalOpen(true)
  }

  const resendInviteAction = [
    {
      key: "edit",
      type: "edit",
      name: formatMessage({
        id: "apps.orders.order-search.cancelOrder"
      }),
      disabled: !get(detailedOrder, "cancellable", true),
      onClick: onCancelOrderClicked
    }
  ]

  const renderHeader = (publicId, coreId, externalReference) => {
    return (
      <Header>
        <OrderInfoDiv>
          <PublicIdRow>
            <PublicIdDiv>
              Order {publicId}
              {detailedOrder.state === TRIP_STATUS.FAILED && (
                <>
                  {" - "}
                  <FailedText>
                    {formatMessage({ id: "apps.order-search.title.failed" })}
                  </FailedText>
                </>
              )}
            </PublicIdDiv>
          </PublicIdRow>
          <Row>
            <FrogneIdRow>
              {!isUndefined(externalReference) &&
                Object.keys(externalReference).map((key, index) => {
                  if (!OrderSearchUtils.isOrderReference(key)) {
                    return
                  }
                  return (
                    <FrogneId key={index}>
                      {OrderSearchUtils.resolveExternalRefPrettyName(key)} -{" "}
                      {externalReference[key]}
                    </FrogneId>
                  )
                })}
              <FrogneId>Core Id: {coreId}</FrogneId>
            </FrogneIdRow>
          </Row>
        </OrderInfoDiv>
        <ButtonDiv>
          <DotsDropdown
            actions={[...resendInviteAction]}
            enableCustomStyling={true}
            buttonText={formatMessage({ id: "common.options" })}
            buttonStyle={{
              backgroundColor: "#ffcc00",
              color: "#000000"
            }}
          />
        </ButtonDiv>
      </Header>
    )
  }

  const onBackClicked = () => {
    props.history.goBack()
  }

  const nonEmptyContent = () => {
    const fromAddress = get(detailedOrder, "fromAddress", {})
    const toAddress = get(detailedOrder, "toAddress", {})
    const viaAddresses = get(detailedOrder, "viaAddress", [])
    const booker = bookerObjToName(get(detailedOrder, "booker", {}))
    const driver = driverObjToName(get(detailedOrder, "driver", {}))
    const vehicleMake = vehicleObjToText(get(detailedOrder, "vehicle", {}))
    const messageToDriver = get(detailedOrder, "traveler.comment", "-")
    return (
      <Container>
        {renderHeader(
          get(detailedOrder, "publicId"),
          get(detailedOrder, "id"),
          get(detailedOrder, "externalReference")
        )}
        <ContentRow>
          <ContentColLeft>
            <InfoContainer data-cy="cy_orderInfoContainer1">
              {renderOrderSummary(
                fromAddress,
                viaAddresses,
                toAddress,
                get(detailedOrder, "pickupTime", "-"),
                get(detailedOrder, "travelNow"),
                get(detailedOrder, "product", {}),
                messageToDriver !== "" ? messageToDriver : "-",
                get(detailedOrder, "economyContent.customerPrice.amount", "-"),
                get(
                  detailedOrder,
                  "economyContent.customerPrice.currency",
                  "-"
                ),
                get(detailedOrder, "fleet.displayName", "-")
              )}
            </InfoContainer>
            <InfoContainer data-cy="cy_orderInfoContainer2">
              {renderTravelerSummary(
                get(detailedOrder, "traveler.name", "-"),
                get(detailedOrder, "traveler.phoneNumber", "-"),
                get(detailedOrder, "traveler.id"),
                driver,
                get(detailedOrder, "driver.id"),
                get(detailedOrder, "driver.phoneNumber", "-"),
                booker,
                get(detailedOrder, "booker.id"),
                get(detailedOrder, "booker.phoneNumber", "-"),
                get(detailedOrder, "vehicle.taxiNumber", "-"),
                get(detailedOrder, "vehicle.registrationNumber", "-"),
                vehicleMake
              )}
            </InfoContainer>
          </ContentColLeft>
          <ContentColRight>
            {detailedOrder.shouldEnableManualHandle && (
              <InfoContainer>
                <OrderHandlePanel />
              </InfoContainer>
            )}
            {detailedOrder.rating && (
              <InfoContainer>
                <RatingPanel />
              </InfoContainer>
            )}
            <InfoContainer>
              <EventPanel />
            </InfoContainer>
            <InfoContainer>
              <PaymentPanel data-cy="cy_orderPaymentPanel" />
            </InfoContainer>
          </ContentColRight>
        </ContentRow>
        <WideCard>
          <Button onClick={onBackClicked}>
            {formatMessage({ id: "common.back" })}
          </Button>
        </WideCard>
        <ConfirmCancelOrderModal
          isVisible={isConfirmCancelModalOpen}
          onConfirm={onCancelOrderConfirmed}
          onCancel={onCancelOrderRejected}
        >
          Are you sure
        </ConfirmCancelOrderModal>
      </Container>
    )
  }

  const errorContent = () => {
    return (
      <Container>
        <WideCard>
          <Button onClick={onBackClicked}>
            {formatMessage({ id: "common.back" })}
          </Button>
        </WideCard>
      </Container>
    )
  }

  const emptyOrder = isEmpty(detailedOrder)

  const renderContent = () => {
    if (loading) {
      return <WideSpinner />
    }
    {
      return emptyOrder ? (
        errorContent()
      ) : ajaxPending ? (
        <Spin>{nonEmptyContent()}</Spin>
      ) : (
        nonEmptyContent()
      )
    }
  }

  return <div>{renderContent()}</div>
}

export default (withRouter(injectIntl(observer(OrderDetailView))): any)
